import type { Question } from "@/commonTypes/question";
import type {
  ApiService,
  PageViewData,
  FriendlyCaptchaVerificationPayload,
  ResponsePayload,
  UtmsData,
  PollResultOutcome,
  PdfResponse,
  ExperimentDefinition,
  QuestionnaireResponse,
  SelfieUploadUrlResponse,
} from "./types";
import { mockQuestionnaire } from "./mocks/questionnaire";
import { mockExperiments } from "./mocks/experiments";

/** Simulated network delay in milliseconds */
const MOCK_DELAY_MS = 100;

/**
 * Mock questionnaire data keyed by ID
 */
const mockQuestionnaireData: Record<
  string,
  { questions: Question[]; experiments: ExperimentDefinition[] }
> = {
  "sleep-assessment-pro-v1": {
    questions: mockQuestionnaire,
    experiments: mockExperiments,
  },
};

/** Track when polling first started per session */
const mockPollStartTimes: Record<string, number> = {};

/** How long to simulate processing before returning a result (in milliseconds) */
const MOCK_PROCESSING_DELAY_MS = 9000;
/**
 * Helper to simulate network latency
 */
function delay(ms: number = MOCK_DELAY_MS): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mock API service implementation - returns fake data for development/testing
 */
export const mockApiService: ApiService = {
  async getQuestions(questionnaireId: string): Promise<QuestionnaireResponse> {
    await delay();
    const data = mockQuestionnaireData[questionnaireId];
    if (!data) {
      throw new Error(`Questionnaire not found: ${questionnaireId}`);
    }

    return data;
  },

  async checkForResult(
    sessionId: string,
    _inferenceId: string,
  ): Promise<PollResultOutcome> {
    await delay();
    if (!mockPollStartTimes[sessionId]) {
      mockPollStartTimes[sessionId] = Date.now();
    }
    const elapsed = Date.now() - mockPollStartTimes[sessionId];
    if (elapsed < MOCK_PROCESSING_DELAY_MS) {
      return { status: "pending" };
    }
    return {
      status: "ready",
      result: {
        symptomCards: [
          {
            id: "osa",
            riskLabelKey: "osa.risk-label-high",
            titleKey: "osa.title",
            descriptionIntroKey: "osa.description-intro",
            ctaLabelKey: "osa.cta-label",
            expandedDescriptionKey: "osa.expanded-description",
            imageUrl: "symptom_osa",
            treatmentOptionsLabelKey: "osa.treatment-options-label",
            explainabilityBullets: [
              { key: "explainability.your-breathing-patterns-while-sleeping" },
              { key: "explainability.how-loud-you-snore" },
            ],
          },
          {
            id: "insomnia",
            riskLabelKey: "you-are-at-risk",
            titleKey: "insomnia.title",
            descriptionIntroKey: "insomnia.description-intro",
            ctaLabelKey: "insomnia.cta-label",
            expandedDescriptionKey: "insomnia.expanded-description",
            imageUrl: "symptom_insomnia",
            explainabilityBullets: [
              { key: "explainability.your-difficulty-in-sleeping" },
            ],
          },
          {
            id: "snoring",
            riskLabelKey: "you-are-at-risk",
            titleKey: "snoring.title",
            descriptionIntroKey: "snoring.description-intro",
            ctaLabelKey: "snoring.cta-label",
            expandedDescriptionKey: "snoring.expanded-description",
            imageUrl: "symptom_snoring",
            explainabilityBullets: [
              { key: "explainability.answering-yes-to-snoring" },
            ],
          },
          {
            id: "fragmented-sleep",
            riskLabelKey: "you-are-at-risk",
            titleKey: "fragmented-sleep.title",
            descriptionIntroKey: "fragmented-sleep.description-intro",
            ctaLabelKey: "fragmented-sleep.cta-label",
            expandedDescriptionKey: "fragmented-sleep.expanded-description",
            imageUrl: "symptom_fragmented_sleep",
            explainabilityBullets: [
              { key: "explainability.how-you-described-your-sleep-quality" },
            ],
          },
          {
            id: "bruxism",
            riskLabelKey: "you-are-at-risk",
            titleKey: "bruxism.title",
            descriptionIntroKey: "bruxism.description-intro",
            ctaLabelKey: "bruxism.cta-label",
            expandedDescriptionKey: "bruxism.expanded-description",
            imageUrl: "symptom_bruxism",
            explainabilityBullets: [
              {
                key: "explainability.waking-up-with-aching-jaw-or-being-told-you-grind-your-teeth",
              },
            ],
          },
          {
            id: "restless-legs",
            riskLabelKey: "you-are-at-risk",
            titleKey: "restless-legs.title",
            descriptionIntroKey: "restless-legs.description-intro",
            ctaLabelKey: "restless-legs.cta-label",
            expandedDescriptionKey: "restless-legs.expanded-description",
            imageUrl: "symptom_restless_legs",
            explainabilityBullets: [
              {
                key: "explainability.needing-to-move-your-legs-to-be-comfortable",
              },
            ],
          },
        ],
      },
    };
  },

  async postPageView(data: PageViewData): Promise<void> {
    await delay();
    if (process.env.NODE_ENV === "development") {
      console.log("[Mock Page View]", data);
    }
  },

  async postUtms(data: UtmsData): Promise<void> {
    await delay();
    if (process.env.NODE_ENV === "development") {
      console.log("[Mock UTM Analytics]", data);
    }
  },

  async postResponse(data: ResponsePayload): Promise<string | null> {
    await delay();
    // Log response in development for debugging
    if (process.env.NODE_ENV === "development") {
      console.log(`[Mock Response - ${data.event ?? "completed"}]`, {
        ...data,
        experimentAssignments: data.experimentAssignments,
      });
    }
    // Only seed poll time for completed events
    if (!data.event || data.event === "completed") {
      mockPollStartTimes[data.id] = Date.now();
      return crypto.randomUUID();
    }
    return null;
  },

  async verifyFriendlyCaptcha(
    data: FriendlyCaptchaVerificationPayload,
  ): Promise<boolean> {
    await delay();
    return data.friendlyCaptchaSolution.trim().length > 0;
  },

  async generatePdf(
    sessionId: string,
    _inferenceId: string,
  ): Promise<PdfResponse> {
    await delay();
    // Return a mock PDF URL (could point to a sample PDF for testing)
    console.log("[Mock PDF Request]", sessionId);
    return {
      session_id: sessionId,
      pdf_url: "/sample_report.pdf",
    };
  },

  async getSelfieUploadUrl(
    sessionId: string,
  ): Promise<SelfieUploadUrlResponse> {
    await delay();
    const mockKey = `selfies/${sessionId}/${crypto.randomUUID()}.png`;
    return {
      upload_url: `https://mock-s3.example.com/${mockKey}`,
      s3_key: mockKey,
    };
  },
};
